import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BellAlertIcon,
  FireIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassCircleIcon,
  DocumentTextIcon,
  ServerStackIcon,
  ShieldExclamationIcon,
  BoltIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";
import { useSOC } from "../context/SOCContext";
import KpiCard from "../ui/KpiCard";
import { SeverityBadge, StatusBadge } from "../ui/Badges";
import ApiLoader from "../../ui/ApiLoader";
import NotificationCenter from "../../ui/NotificationCenter";

const SEVERITY_ORDER = ["critical", "high", "medium", "low"];

const barColor = {
  critical: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-yellow-500",
  low: "bg-blue-500",
};

const Dashboard = ({ onNavigate }) => {
  const navigate = useNavigate();
  const {
    alerts,
    assets,
    incidents,
    investigations,
    events,
    responses,
    scan,
    runScanNow,
    dashboardStats,
    loading,
    errors,
  } = useSOC();

  // Prefer the authoritative counts from GET /api/soc/dashboard/ where
  // its definition matches exactly; "Events Today" (last 24h) and
  // "Response Actions" have no equivalent backend field (the
  // dashboard endpoint's events_count is an all-time total, a
  // different metric), so those stay computed from the fetched lists.
  const kpis = useMemo(() => {
    const critical = alerts.filter((a) => a.severity === "critical").length;
    const eventsToday = events.filter(
      (e) => new Date(e.timestamp) > new Date(Date.now() - 24 * 3600 * 1000),
    ).length;
    return {
      totalAlerts: dashboardStats?.total_alerts ?? alerts.length,
      criticalAlerts: dashboardStats?.critical_alerts ?? critical,
      highAlerts:
        dashboardStats?.high_alerts ??
        alerts.filter((a) => a.severity === "high").length,
      openIncidents:
        dashboardStats?.open_incidents ??
        incidents.filter((i) => !["Resolved", "Closed"].includes(i.status))
          .length,
      activeInvestigations:
        dashboardStats?.active_investigations ??
        investigations.filter(
          (i) => !["Completed", "Closed"].includes(i.status),
        ).length,
      eventsToday,
      totalEvents: dashboardStats?.events_count ?? events.length,
      monitoredAssets: dashboardStats?.monitored_assets ?? assets.length,
      highRiskAssets:
        dashboardStats?.high_risk_assets ??
        assets.filter((a) => a.riskScore >= 70).length,
      responseActions: responses.length,
    };
  }, [
    dashboardStats,
    alerts,
    assets,
    incidents,
    investigations,
    events,
    responses,
  ]);

  const severityBreakdown = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    alerts.forEach((a) => {
      if (counts[a.severity] !== undefined) counts[a.severity]++;
    });
    return counts;
  }, [alerts]);

  const incidentOverview = useMemo(() => {
    const buckets = { Open: 0, Investigating: 0, Contained: 0, Resolved: 0 };
    incidents.forEach((i) => {
      if (i.status === "Open") buckets.Open++;
      else if (["Investigating", "Eradication", "Recovery"].includes(i.status))
        buckets.Investigating++;
      else if (i.status === "Contained") buckets.Contained++;
      else if (["Resolved", "Closed"].includes(i.status)) buckets.Resolved++;
    });
    return buckets;
  }, [incidents]);

  const trend = useMemo(() => {
    const now = Date.now();
    const buckets = [
      { label: "Last 24h", ms: 24 * 3600 * 1000 },
      { label: "Last 7d", ms: 7 * 24 * 3600 * 1000 },
      { label: "Last 30d", ms: 30 * 24 * 3600 * 1000 },
    ];
    return buckets.map((b) => ({
      label: b.label,
      count: alerts.filter(
        (a) => now - new Date(a.detectionTime).getTime() <= b.ms,
      ).length,
    }));
  }, [alerts]);

  const recentCritical = alerts
    .filter((a) => a.severity === "critical" || a.severity === "high")
    .slice(0, 8);
  const authEvents = events
    .filter((e) => e.type === "Login Success" || e.type === "Login Failed")
    .slice(0, 8);
  const fimEvents = events.filter((e) => e.type.startsWith("File")).slice(0, 8);
  const topRiskyAssets = [...assets]
    .sort((a, b) => b.riskScore - a.riskScore)
    .slice(0, 8);
  const latestEvents = events.slice(0, 8);

  const threatTally = useMemo(() => {
    const tally = {};
    alerts.forEach((a) => {
      tally[a.name] = (tally[a.name] || 0) + 1;
    });
    return Object.entries(tally)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [alerts]);

  if (loading.dashboard && !dashboardStats) {
    return (
      <ApiLoader
        variant="spinner"
        label="Loading Dashboard..."
        className="py-20"
      />
    );
  }

  return (
    <div className="space-y-6">
      {errors.dashboard && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {errors.dashboard}
        </div>
      )}

      {/* KPI Cards + Notifications */}
      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        <div className="w-full lg:w-[28rem] flex-shrink-0 h-[380px]">
          <NotificationCenter module="virtual_soc" title="SOC Notifications" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 flex-1">
          <div onClick={() => navigate("/SOC?tab=alerts")} role="button" tabIndex={0} className="cursor-pointer">
            <KpiCard
              label="Total Alerts"
              value={kpis.totalAlerts}
              icon={BellAlertIcon}
              tone="blue"
            />
          </div>
          <div onClick={() => navigate("/SOC?tab=alerts")} role="button" tabIndex={0} className="cursor-pointer">
            <KpiCard
              label="Critical Alerts"
              value={kpis.criticalAlerts}
              icon={FireIcon}
              tone="red"
            />
          </div>
          <div onClick={() => navigate("/SOC?tab=incidents")} role="button" tabIndex={0} className="cursor-pointer">
            <KpiCard
              label="Open Incidents"
              value={kpis.openIncidents}
              icon={ExclamationTriangleIcon}
              tone="orange"
            />
          </div>
          <div onClick={() => navigate("/SOC?tab=investigations")} role="button" tabIndex={0} className="cursor-pointer">
            <KpiCard
              label="Active Investigations"
              value={kpis.activeInvestigations}
              icon={MagnifyingGlassCircleIcon}
              tone="purple"
            />
          </div>
          <div onClick={() => navigate("/SOC?tab=events")} role="button" tabIndex={0} className="cursor-pointer">
            <KpiCard
              label="Events Today"
              value={kpis.eventsToday}
              icon={DocumentTextIcon}
              tone="cyan"
            />
          </div>
          <div onClick={() => navigate("/SOC?tab=events")} role="button" tabIndex={0} className="cursor-pointer">
            <KpiCard
              label="Total Events"
              value={kpis.totalEvents}
              icon={DocumentTextIcon}
              tone="cyan"
            />
          </div>
          <div onClick={() => navigate("/SOC?tab=dashboard")} role="button" tabIndex={0} className="cursor-pointer">
            <KpiCard
              label="Monitored Assets"
              value={kpis.monitoredAssets}
              icon={ServerStackIcon}
              tone="slate"
            />
          </div>
          <div onClick={() => navigate("/SOC?tab=dashboard")} role="button" tabIndex={0} className="cursor-pointer">
            <KpiCard
              label="High Risk Assets"
              value={kpis.highRiskAssets}
              icon={ShieldExclamationIcon}
              tone="yellow"
            />
          </div>
          <div onClick={() => navigate("/SOC?tab=response")} role="button" tabIndex={0} className="cursor-pointer">
            <KpiCard
              label="Response Actions"
              value={kpis.responseActions}
              icon={BoltIcon}
              tone="green"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scan Status Card */}
        <div className="bg-white rounded-xl shadow p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Security Scan
          </h3>
          <div className="flex items-center gap-2 mb-3">
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                scan.status === "Running"
                  ? "bg-yellow-400 animate-pulse"
                  : scan.status === "Completed"
                    ? "bg-green-500"
                    : "bg-gray-300"
              }`}
            />
            <span className="text-sm text-gray-600">Status: {scan.status}</span>
          </div>
          <div className="space-y-1 text-sm text-gray-500 mb-4">
            <p>
              Last Scan:{" "}
              <span className="text-gray-700">
                {scan.lastScanTime
                  ? new Date(scan.lastScanTime).toLocaleTimeString()
                  : "-"}
              </span>
            </p>
            <p>
              Next Scan:{" "}
              <span className="text-gray-700">
                {new Date(scan.nextScanTime).toLocaleTimeString()}
              </span>
            </p>
            <p>
              Issues Found:{" "}
              <span className="text-gray-700">{scan.issuesFound}</span>
            </p>
            {scan.latestDetection && (
              <p>
                Latest Detection:{" "}
                <SeverityBadge severity={scan.latestDetection.severity} />{" "}
                <span className="text-xs text-gray-400">
                  {new Date(scan.latestDetection.time).toLocaleTimeString()}
                </span>
              </p>
            )}
          </div>
          <button
            onClick={runScanNow}
            disabled={scan.status === "Running"}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium"
          >
            <PlayIcon className="w-4 h-4" /> Run Scan Now
          </button>
        </div>

        {/* Alert Trend */}
        <div className="bg-white rounded-xl shadow p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Alert Trend
          </h3>
          <div className="space-y-3">
            {trend.map((t) => (
              <div key={t.label}>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>{t.label}</span>
                  <span>{t.count}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{
                      width: `${Math.min(100, (t.count / Math.max(1, kpis.totalAlerts)) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Alert Severity */}
        <div className="bg-white rounded-xl shadow p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Alert Severity
          </h3>
          <div className="space-y-3">
            {SEVERITY_ORDER.map((sev) => (
              <div key={sev}>
                <div className="flex justify-between text-xs text-gray-500 mb-1 capitalize">
                  <span>{sev}</span>
                  <span>{severityBreakdown[sev]}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${barColor[sev]}`}
                    style={{
                      width: `${Math.min(100, (severityBreakdown[sev] / Math.max(1, kpis.totalAlerts)) * 100)}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Incident Overview */}
      <div className="bg-white rounded-xl shadow p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">
          Incident Overview
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(incidentOverview).map(([label, count]) => (
            <div
              key={label}
              className="bg-gray-50 border border-gray-100 rounded-lg p-4 text-center"
            >
              <p className="text-2xl font-bold text-gray-800">{count}</p>
              <p className="text-xs text-gray-500 mt-1">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Critical Alerts */}
        <div className="bg-white rounded-xl shadow p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              Recent Critical Alerts
            </h3>
            <button
              onClick={() => onNavigate("alerts")}
              className="text-xs text-blue-600 hover:underline"
            >
              View all
            </button>
          </div>
          <div className="space-y-2">
            {recentCritical.length === 0 && (
              <p className="text-sm text-gray-400">No critical/high alerts.</p>
            )}
            {recentCritical.map((a) => (
              <button
                key={a.id}
                onClick={() => onNavigate("alerts", a.id)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 border border-gray-100 rounded-lg hover:border-gray-200 text-left"
              >
                <div className="min-w-0">
                  <p className="text-sm text-gray-700 truncate">{a.name}</p>
                  <p className="text-xs text-gray-400">
                    {a.assetName} ·{" "}
                    {new Date(a.detectionTime).toLocaleTimeString()}
                  </p>
                </div>
                <SeverityBadge severity={a.severity} />
              </button>
            ))}
          </div>
        </div>

        {/* Top Threats */}
        <div className="bg-white rounded-xl shadow p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Top Threats
          </h3>
          <div className="space-y-3">
            {threatTally.map(([name, count], idx) => (
              <div key={name} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 text-xs font-bold flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <span className="text-sm text-gray-700">{name}</span>
                </div>
                <span className="text-sm text-gray-400">{count}</span>
              </div>
            ))}
            {threatTally.length === 0 && (
              <p className="text-sm text-gray-400">No threats recorded.</p>
            )}
          </div>
        </div>

        {/* Authentication Activity */}
        <div className="bg-white rounded-xl shadow p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Authentication Activity
          </h3>
          <div className="space-y-2">
            {authEvents.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-gray-700">
                  {e.user} · {e.type}
                </span>
                <StatusBadge status={e.result} />
              </div>
            ))}
            {authEvents.length === 0 && (
              <p className="text-sm text-gray-400">No authentication events.</p>
            )}
          </div>
        </div>

        {/* FIM Activity */}
        <div className="bg-white rounded-xl shadow p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            FIM / File Integrity Activity
          </h3>
          <div className="space-y-2">
            {fimEvents.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-gray-700">
                  {e.assetName} · {e.type}
                </span>
                <span className="text-xs text-gray-400">
                  {new Date(e.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
            {fimEvents.length === 0 && (
              <p className="text-sm text-gray-400">No file integrity events.</p>
            )}
          </div>
        </div>

        {/* Top Risky Assets */}
        <div className="bg-white rounded-xl shadow p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              Top Risky Assets
            </h3>
          </div>
          <div className="space-y-2">
            {topRiskyAssets.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-gray-700">{a.name}</span>
                <span className="text-gray-400">{a.riskScore}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Latest Security Events */}
        <div className="bg-white rounded-xl shadow p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">
              Latest Security Events
            </h3>
            <button
              onClick={() => onNavigate("events")}
              className="text-xs text-blue-600 hover:underline"
            >
              View all
            </button>
          </div>
          <div className="space-y-2">
            {latestEvents.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-gray-700 truncate">{e.type}</span>
                <span className="text-xs text-gray-400">
                  {new Date(e.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
